import { LucideIcon, TrendingUp } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';

interface StatCardProps {
  title: string;
  value: string;
  change?: string;
  changeLabel?: string;
  trend?: 'up' | 'neutral';
  icon: LucideIcon;
  bgColor: string;
  iconColor: string;
  chartData?: number[];
  chartColor?: string;
  gradientColor?: string;
  index?: number;
}

export const StatsCard = ({ 
  title, 
  value, 
  change, 
  changeLabel, 
  trend, 
  icon: Icon,
  bgColor,
  iconColor,
  chartData = [],
  chartColor = '#3b82f6',
  gradientColor = '#3b82f6',
  index = 0
}: StatCardProps) => {
  const formattedChartData = chartData.map((value) => ({ value }));

  return (
    <div className="bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow overflow-hidden">
      <div className="p-4 pb-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <h5 className="text-xs text-gray-600 dark:text-gray-400 font-medium uppercase mb-2">
              {title}
            </h5>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-1.5">
              {value}
            </h3>
            {change && changeLabel && (
              <div className="flex items-center gap-1 text-xs">
                {trend === 'up' ? (
                  <TrendingUp className="w-3.5 h-3.5 text-green-600 dark:text-green-400" />
                ) : null}
                <span className={trend === 'up' ? 'text-green-600 dark:text-green-400 font-medium' : 'text-gray-600 dark:text-gray-400 font-medium'}>
                  {change}
                </span>
                <span className="text-gray-500 dark:text-gray-400">
                  {changeLabel}
                </span>
              </div>
            )}
          </div>
          <div className={`${bgColor} p-2.5 rounded flex-shrink-0`}>
            <Icon className={`w-6 h-6 ${iconColor}`} />
          </div>
        </div>
      </div>

      <div className="h-18 -mb-2">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={formattedChartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
              <defs>
                <linearGradient id={`gradientColor-${index}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={gradientColor} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={gradientColor} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke={chartColor}
                strokeWidth={2}
                fill={`url(#gradientColor-${index})`}
                animationDuration={500}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
    </div>
  );
};
